<template>
  <transition name="fade">
    <div class="modal-overlay" @click.self="emit('close')">
      <div class="modal-container" role="dialog" aria-modal="true" aria-labelledby="modal-title">
        <div class="modal-header">
          <h2 id="modal-title" class="modal-title">{{ title }}</h2>
          <button class="modal-close" @click="emit('close')" :aria-label="t('details.close')">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>
        
        <div class="modal-body">
          <div class="alert-icon">
            <i class="fa-solid fa-triangle-exclamation"></i>
          </div>
          
          <p class="modal-message">{{ message }}</p>
          
          <div class="product-info" v-if="product">
            <div class="info-row">
              <span class="info-label">{{ t('details.product') }}</span>
              <span class="info-value">{{ product.title }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">{{ t('details.days_since_posted') }}</span>
              <span class="info-value">{{ toFa(product.daysPassed) }} {{ t('details.days') }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">{{ t('details.price') }}</span>
              <span class="info-value">{{ toFa(formatNumber(product.price)) }} {{ t('details.currency') }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">{{ t('details.available_quantity') }}</span>
              <span class="info-value">{{ toFa(formatNumber(product.count)) }} {{ product.unit }}</span>
            </div>
          </div>
          
          <p class="modal-hint">{{ t('details.order_request_hint') }}</p>
        </div>
        
        <div class="modal-footer">
          <button class="btn-primary" @click="handleConfirm">
            {{ t('details.create_order_request') }}
          </button>
          <button class="btn-secondary" @click="handlePriceInquiry">
            {{ t('details.price_inquiry') }}
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { computed } from 'vue'

const props = defineProps({
  product: {
    type: Object,
    default: () => null
  },
  title: {
    type: String,
    default: ''
  },
  message: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['close', 'confirm', 'price-inquiry'])

const { t } = useI18n()

const formatNumber = (num: number) => {
  return new Intl.NumberFormat('fa-IR').format(num)
}

const toFa = (val: any) => {
  if (val === null || val === undefined) return ''
  return String(val).replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[d])
}

const handleConfirm = () => {
  emit('confirm')
  emit('close')
}

const handlePriceInquiry = () => {
  emit('price-inquiry')
  emit('close')
}
</script>

<style lang="scss" scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  z-index: 1000;
  animation: fadeIn 0.2s ease;
}

.modal-container {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  max-width: 480px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  animation: slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid rgba(226, 214, 189, 0.5);
}

.modal-title {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 800;
  color: #1a1a2e;
}

.modal-close {
  width: 36px;
  height: 36px;
  border: none;
  background: rgba(226, 214, 189, 0.3);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: rgba(192, 57, 43, 0.1);
    color: #c0392b;
  }
}

.modal-body {
  padding: 24px;
  text-align: center;
}

.alert-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: linear-gradient(135deg, #f39c12, #e67e22);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  margin: 0 auto 16px;
  box-shadow: 0 8px 24px rgba(243, 156, 18, 0.3);
}

.modal-message {
  font-size: 1rem;
  line-height: 1.7;
  color: #333;
  margin: 0 0 20px;
  font-weight: 500;
}

.product-info {
  background: #f8f6f3;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
  text-align: right;
  direction: rtl;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid rgba(226, 214, 189, 0.5);
  
  &:last-child {
    border-bottom: none;
  }
}

.info-label {
  font-size: 0.85rem;
  color: #888;
  font-weight: 500;
}

.info-value {
  font-size: 0.9rem;
  font-weight: 700;
  color: #1a1a2e;
  direction: ltr;
}

.modal-hint {
  font-size: 0.85rem;
  color: #888;
  margin: 0;
  line-height: 1.6;
}

.modal-footer {
  display: flex;
  gap: 12px;
  padding: 16px 24px 24px;
  border-top: 1px solid rgba(226, 214, 189, 0.3);
  justify-content: flex-end;
}

.btn-secondary,
.btn-primary,
.btn-inquiry {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border-radius: 100px;
  font-family: inherit;
  font-size: 0.85rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.25s;
  border: none;
}

.btn-secondary {
  background: transparent;
  color: #666;
  border: 1.5px solid rgba(226, 214, 189, 0.6);
  
  &:hover {
    background: #f8f9fa;
    border-color: #888;
    color: #333;
  }
}

.btn-primary {
  background: linear-gradient(135deg, #03a054, #028a48);
  color: #fff;
  box-shadow: 0 4px 14px rgba(3, 160, 84, 0.3);
  
  &:hover {
    background: linear-gradient(135deg, #028a48, #01703c);
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(3, 160, 84, 0.35);
  }
  
  &:active {
    transform: scale(0.97);
  }
}

.btn-inquiry {
  background: linear-gradient(135deg, #e8a838, #d4902a);
  color: #fff;
  box-shadow: 0 4px 14px rgba(232, 168, 56, 0.3);
  
  &:hover {
    background: linear-gradient(135deg, #d4902a, #b87a20);
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(232, 168, 56, 0.35);
  }
  
  &:active {
    transform: scale(0.97);
  }
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideUp {
  from { 
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
  to { 
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>